import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { RiDeleteBinLine, RiPencilLine, RiStopFill } from 'react-icons/ri';
import { vizMarkdownComponents } from '../../utils/markdownComponents';
import { WorkflowIcon } from './WorkflowIconPicker';

const API_BASE = process.env.REACT_APP_AUTH_BACKEND_URL || '';

/* ── Copy-to-clipboard button ────────────────────────────────── */

/**
 * Button that writes text to the system clipboard and shows a
 * brief "Copied!" confirmation in place of the label.
 *
 * @param {string} text - Plain text to copy.
 */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      title="Copy to clipboard"
      style={{
        padding: '2px 7px',
        fontSize: '0.65rem',
        border: '1px solid var(--border-color)',
        borderRadius: 4,
        background: 'transparent',
        color: copied
          ? 'var(--success-text-color, #065f46)'
          : 'var(--text-secondary)',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

/* ── Helpers ─────────────────────────────────────────────────── */

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

function fmtDuration(ms) {
  if (ms == null) return null;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** Derive a human-readable label for the node type badge. */
function nodeTypeLabel(type) {
  const MAP = {
    input: 'Input', output: 'Output',
    agent: 'Agent', tool: 'Tool', logic: 'Logic',
  };
  return MAP[type] || type;
}

/* ── Status badge ─────────────────────────────────────────────── */

const STATUS_STYLES = {
  completed:  { bg: '#d1fae5', color: '#065f46' },
  failed:     { bg: '#fee2e2', color: '#991b1b' },
  cancelled:  { bg: '#fef3c7', color: '#92400e' },
  pending:    { bg: '#e0e7ff', color: '#3730a3' },
  running:    { bg: '#dbeafe', color: '#1e40af' },
  skipped:    { bg: '#f3f4f6', color: '#6b7280' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      fontSize: '0.68rem', fontWeight: 600, padding: '2px 7px',
      borderRadius: 99, background: s.bg, color: s.color,
      textTransform: 'uppercase', letterSpacing: '0.02em',
      flexShrink: 0,
    }}>
      {status}
    </span>
  );
}

const MOBILE_STATUS_ICON = {
  completed: { symbol: '✓', color: '#059669' },
  failed:    { symbol: '✗', color: '#dc2626' },
  cancelled: { symbol: '✗', color: '#d97706' },
  pending:   { symbol: '○', color: '#6366f1' },
  running:   { symbol: '⏳', color: '#2563eb' },
};

function MobileStatusIcon({ status }) {
  const s = MOBILE_STATUS_ICON[status] || { symbol: '?', color: '#6b7280' };
  return (
    <span style={{
      fontSize: '0.82rem', fontWeight: 700,
      color: s.color, flexShrink: 0, lineHeight: 1,
    }}>
      {s.symbol}
    </span>
  );
}

/* ── Node I/O drawer ──────────────────────────────────────────── */

function NodeIODrawer({ nodeResult, nodeLabel }) {
  const { input_snapshot, content, variables, error } = nodeResult;
  const hasVars = variables && Object.keys(variables).length > 0;
  const privateVars = new Set(['_condition_result', '_route_score', '_route_taken']);
  const publicVars = hasVars
    ? Object.entries(variables).filter(([k]) => !privateVars.has(k))
    : [];

  return (
    <div style={{
      marginTop: 6, padding: '10px 12px',
      background: 'var(--hover-color)',
      border: '1px solid var(--border-color)',
      borderRadius: 6, fontSize: '0.75rem',
      lineHeight: 1.55,
    }}>
      {input_snapshot && (
        <section style={{ marginBottom: 10 }}>
          <div style={{
            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: 'var(--text-secondary)',
            marginBottom: 4,
          }}>
            Input
          </div>
          <pre style={{
            margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: 'monospace', fontSize: '0.73rem',
            color: 'var(--text-color)',
          }}>
            {input_snapshot}
          </pre>
        </section>
      )}

      {error && (
        <section style={{ marginBottom: 10 }}>
          <div style={{
            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: '#991b1b', marginBottom: 4,
          }}>
            Error
          </div>
          <pre style={{
            margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: 'monospace', fontSize: '0.73rem', color: '#991b1b',
          }}>
            {error}
          </pre>
        </section>
      )}

      {publicVars.length > 0 && (
        <section style={{ marginBottom: content ? 10 : 0 }}>
          <div style={{
            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: 'var(--text-secondary)',
            marginBottom: 4,
          }}>
            Output Variables
          </div>
          {publicVars.map(([k, v]) => {
            const raw = typeof v === 'string'
              ? v
              : JSON.stringify(v, null, 2);
            const isText = typeof v === 'string';
            return (
              <div key={k} style={{ marginBottom: 8 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}>
                  <span style={{
                    fontFamily: 'monospace', fontSize: '0.7rem',
                    color: 'var(--primary-color)', fontWeight: 600,
                  }}>
                    {k}:
                  </span>
                  <CopyButton text={raw} />
                </div>
                {isText ? (
                  <div className="wf-run-markdown">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeSanitize]}
                      components={vizMarkdownComponents}
                    >
                      {v}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <pre style={{
                    margin: 0, whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace', fontSize: '0.72rem',
                    color: 'var(--text-color)',
                  }}>
                    {raw}
                  </pre>
                )}
              </div>
            );
          })}
        </section>
      )}

      {content && !publicVars.length && (
        <section>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}>
            <div style={{
              fontSize: '0.68rem', fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-secondary)',
            }}>
              Output
            </div>
            <CopyButton text={content} />
          </div>
          <div className="wf-run-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={vizMarkdownComponents}
            >
              {content}
            </ReactMarkdown>
          </div>
        </section>
      )}
    </div>
  );
}

/* ── Node row inside a run ────────────────────────────────────── */

const STATUS_ICON = {
  completed: '✓', failed: '✕', skipped: '–',
  pending: '○', running: '⏳',
};

function NodeRow({ nodeResult, label, canExpand }) {
  const [open, setOpen] = useState(false);
  const { status, node_type, duration_ms } = nodeResult;
  const icon = STATUS_ICON[status] || '?';
  const iconColor = STATUS_STYLES[status]?.color || '#6b7280';

  return (
    <div style={{ borderBottom: '1px solid var(--border-color)' }}>
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => canExpand && setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: 7, padding: '6px 10px', border: 'none',
          background: 'transparent', cursor: canExpand ? 'pointer' : 'default',
          textAlign: 'left',
        }}
      >
        <span style={{
          fontSize: '0.7rem', fontWeight: 700, color: iconColor,
          width: 14, flexShrink: 0, textAlign: 'center',
        }}>
          {icon}
        </span>
        <span style={{
          fontSize: '0.76rem', color: 'var(--text-color)',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
        <span style={{
          fontSize: '0.67rem', color: 'var(--text-secondary)',
          flexShrink: 0,
        }}>
          {nodeTypeLabel(node_type)}
        </span>
        {duration_ms != null && (
          <span style={{
            fontSize: '0.67rem', color: 'var(--text-secondary)',
            flexShrink: 0, minWidth: 44, textAlign: 'right',
          }}>
            {fmtDuration(duration_ms)}
          </span>
        )}
        {canExpand && (
          <span style={{
            fontSize: '0.6rem', color: 'var(--text-secondary)',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s', flexShrink: 0,
          }}>
            ▶
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: '0 10px 8px 31px' }}>
          <NodeIODrawer nodeResult={nodeResult} label={label} />
        </div>
      )}
    </div>
  );
}

/* ── Single run row ───────────────────────────────────────────── */

function RunRow({ run, nodes, onRerun, rerunning, onRename, onDelete, onStop, showWorkflowName, onSelectWorkflow, isMobile }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [stopping, setStopping] = useState(false);

  // Lazy-load full run data (node_results_json, output_json) on first
  // expand when the list endpoint omits them for bandwidth efficiency.
  const [fullRun, setFullRun] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);

  React.useEffect(() => {
    // Only fetch when the row is open AND node_results_json is absent from prop
    if (!open || run.node_results_json != null || fullRun || loadingFull) return;
    const wfId = run.workflow_id;
    if (!wfId) return;
    setLoadingFull(true);
    fetch(
      `${API_BASE}/workflows/${wfId}/runs/${run.id}`,
      { credentials: 'include' },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setFullRun(data); })
      .catch(() => {})
      .finally(() => setLoadingFull(false));
  }, [open, run, fullRun, loadingFull]);

  // Use lazily-fetched data when available, fall back to prop data
  const nodeResultsJson = fullRun?.node_results_json ?? run.node_results_json ?? null;

  /* Build an ordered node list from node_results_json, sorted by started_at */
  const orderedNodes = React.useMemo(() => {
    const results = nodeResultsJson || {};
    return Object.entries(results)
      .map(([nodeId, result]) => {
        const canvasNode = (nodes || []).find((n) => n.id === nodeId);
        return {
          nodeId,
          label: canvasNode?.data?.label || nodeId,
          result,
        };
      })
      .sort((a, b) => {
        const ta = a.result?.started_at || '';
        const tb = b.result?.started_at || '';
        return ta < tb ? -1 : ta > tb ? 1 : 0;
      });
  }, [nodeResultsJson, nodes]);

  /* Derive run duration from engine-level timestamps when available */
  const runDuration = React.useMemo(() => {
    if (!run.started_at || !run.completed_at) return null;
    try {
      const ms =
        new Date(run.completed_at) - new Date(run.started_at);
      return ms >= 0 ? ms : null;
    } catch (_) { return null; }
  }, [run.started_at, run.completed_at]);

  const isTerminal = ['completed', 'failed', 'cancelled'].includes(
    run.status,
  );

  return (
    <div style={{
      borderBottom: '1px solid var(--border-color)',
      background: open ? 'var(--hover-color)' : 'transparent',
    }}>
      {/* Run header row — status + input label + icon actions only */}
      <div style={{
        display: 'flex', alignItems: 'center',
        gap: 6, padding: '7px 10px',
      }}>
        {/* Expand toggle + label */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center',
            gap: 6, border: 'none', background: 'transparent',
            cursor: 'pointer', textAlign: 'left', padding: 0,
            minWidth: 0,
          }}
        >
          <span style={{
            fontSize: '0.55rem', color: 'var(--text-secondary)',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s', flexShrink: 0,
          }}>
            ▶
          </span>
          {isMobile
            ? <MobileStatusIcon status={run.status} />
            : <StatusBadge status={run.status} />
          }
          {/* Workflow icon — between status and label in all-runs mode */}
          {showWorkflowName && run.workflow_icon && (
            <WorkflowIcon
              iconKey={run.workflow_icon}
              size={12}
              color="var(--text-secondary)"
            />
          )}
          {/* Primary label: custom name or first input value */}
          {editing ? null : (() => {
            const label = run.name || (() => {
              const inp = run.input_json;
              if (inp && typeof inp === 'object') {
                const first = Object.values(inp).find(v => v);
                if (first) return String(first);
              }
              return null;
            })();
            return (
              <span style={{
                fontSize: '0.78rem',
                color: label ? 'var(--text-color)' : 'var(--text-secondary)',
                fontWeight: label ? 500 : 400,
                whiteSpace: 'nowrap', overflow: 'hidden',
                textOverflow: 'ellipsis', flex: 1,
                fontStyle: label ? 'normal' : 'italic',
              }}>
                {label
                  ? (label.length > 38 ? label.slice(0, 38) + '…' : label)
                  : 'No input'}
              </span>
            );
          })()}
          {/* Workflow name — trailing label in all-runs mode */}
          {showWorkflowName && run.workflow_name && (
            <span style={{
              fontSize: '0.66rem', color: 'var(--text-secondary)',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {run.workflow_name.length > 16
                ? run.workflow_name.slice(0, 16) + '…'
                : run.workflow_name}
            </span>
          )}
        </button>

        {/* Inline rename form */}
        {editing && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const name = editName.trim();
              if (name && onRename) await onRename(run.id, name);
              setEditing(false);
            }}
            style={{ flex: 1, display: 'flex', gap: 4 }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
              onBlur={() => setEditing(false)}
              autoFocus
              style={{
                flex: 1, fontSize: '0.72rem', padding: '1px 4px',
                border: '1px solid var(--border-color)',
                borderRadius: 3,
              }}
            />
          </form>
        )}

        {/* Icon-only action buttons */}
        {!editing && (
          <div style={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
            {/* Quick-stop: only shown while the run is still in progress */}
            {!isTerminal && onStop && (
              <button
                type="button"
                title="Stop this run"
                disabled={stopping}
                onClick={async (e) => {
                  e.stopPropagation();
                  setStopping(true);
                  try { await onStop(run.id); }
                  finally { setStopping(false); }
                }}
                style={{
                  background: 'none', border: 'none',
                  cursor: stopping ? 'not-allowed' : 'pointer',
                  color: 'var(--error-text-color, #dc2626)',
                  padding: '2px 3px', display: 'flex', alignItems: 'center',
                  opacity: stopping ? 0.4 : 1,
                }}
              >
                <RiStopFill size={12} />
              </button>
            )}
            <button
              type="button"
              title="Rename run"
              onClick={(e) => { e.stopPropagation(); setEditName(run.name || ''); setEditing(true); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', padding: '2px 3px',
                display: 'flex', alignItems: 'center',
              }}
            >
              <RiPencilLine size={12} />
            </button>
            <button
              type="button"
              title={isTerminal ? 'Delete run' : 'Cancel the run before deleting'}
              disabled={!isTerminal}
              onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm('Delete this run? This cannot be undone.')) return;
                if (onDelete) await onDelete(run.id);
              }}
              style={{
                background: 'none', border: 'none',
                cursor: isTerminal ? 'pointer' : 'not-allowed',
                color: isTerminal
                  ? 'var(--error-text-color, #dc2626)'
                  : 'var(--text-secondary, #aaa)',
                padding: '2px 3px',
                display: 'flex', alignItems: 'center',
              }}
            >
              <RiDeleteBinLine size={12} />
            </button>
            {isTerminal && (
              <button
                type="button"
                title="Rerun"
                disabled={rerunning}
                onClick={(e) => { e.stopPropagation(); onRerun(run.id); }}
                style={{
                  background: 'none', border: 'none',
                  cursor: rerunning ? 'not-allowed' : 'pointer',
                  color: 'var(--text-secondary)', padding: '2px 3px',
                  display: 'flex', alignItems: 'center',
                  opacity: rerunning ? 0.4 : 1,
                  fontSize: '0.85rem',
                }}
              >
                ↺
              </button>
            )}
          </div>
        )}
      </div>

      {/* Node timeline + run metadata (timestamp & duration shown here) */}
      {open && (
        <div style={{
          borderTop: '1px solid var(--border-color)',
          marginBottom: 2,
        }}>
          {/* Run metadata row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '5px 10px 4px',
            fontSize: '0.68rem', color: 'var(--text-secondary)',
          }}>
            <span>{fmtDate(run.created_at)}</span>
            {runDuration != null && <span>{fmtDuration(runDuration)}</span>}
            {onSelectWorkflow && run.workflow_id && (
              <button
                type="button"
                title="Load this workflow on the canvas"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectWorkflow(run.workflow_id);
                }}
                style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: '1px solid var(--border-color)',
                  borderRadius: 4,
                  padding: '2px 7px',
                  fontSize: '0.68rem',
                  color: 'var(--primary-color, #636cff)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Open workflow ↗
              </button>
            )}
          </div>
          {loadingFull ? (
            <div style={{
              padding: '8px 10px', fontSize: '0.75rem',
              color: 'var(--text-secondary)', fontStyle: 'italic',
            }}>
              Loading node details…
            </div>
          ) : orderedNodes.length === 0 ? (
            <div style={{
              padding: '8px 10px', fontSize: '0.75rem',
              color: 'var(--text-secondary)',
            }}>
              No node results recorded.
            </div>
          ) : (
            orderedNodes.map(({ nodeId, label, result }) => {
              const canExpand = !!(
                result.input_snapshot ||
                result.content ||
                result.error ||
                Object.keys(result.variables || {}).length > 0
              );
              return (
                <NodeRow
                  key={nodeId}
                  nodeResult={result}
                  label={label}
                  canExpand={canExpand}
                />
              );
            })
          )}
          {run.error_message && (
            <div style={{
              padding: '6px 10px', fontSize: '0.73rem',
              color: '#991b1b',
              borderTop: '1px solid var(--border-color)',
            }}>
              {run.error_message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────── */

/**
 * Run-history panel for a single workflow.
 *
 * Fetches the 20 most recent runs on mount and whenever the
 * workflow changes.  Provides per-run expand/collapse, per-node
 * input/output inspection, and a Rerun button for terminal runs.
 *
 * @param {string}   props.workflowId - Active workflow UUID.
 * @param {Array}    props.nodes      - Current canvas nodes (for labels).
 * @param {Function} props.onRerun    - Called with (workflowId, runId)
 *   when Rerun is clicked; the hook starts the new run and streams it.
 * @param {boolean}  props.running    - Whether a run is currently active.
 */
export default function WorkflowRunHistory({
  workflowId,
  nodes,
  onRerun,
  onStop,
  onSelectWorkflow,
  running,
  isMobile = false,
}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rerunningId, setRerunningId] = useState(null);

  // Keep a ref so rename/delete/rerun handlers can look up the
  // workflow_id from run data without adding `runs` to their deps.
  const runsRef = useRef([]);
  useEffect(() => { runsRef.current = runs; }, [runs]);

  const _wfIdFor = useCallback(
    (runId) => workflowId || runsRef.current.find((r) => r.id === runId)?.workflow_id,
    [workflowId],
  );

  const handleRenameRun = useCallback(async (runId, name) => {
    const wfId = _wfIdFor(runId);
    if (!wfId) return;
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${wfId}/runs/${runId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name }),
        },
      );
      if (res.ok) {
        setRuns((prev) =>
          prev.map((r) => r.id === runId ? { ...r, name } : r)
        );
      }
    } catch (_) { /* non-fatal */ }
  }, [_wfIdFor]);

  const handleDeleteRun = useCallback(async (runId) => {
    const wfId = _wfIdFor(runId);
    if (!wfId) return;
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${wfId}/runs/${runId}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (res.ok) setRuns((prev) => prev.filter((r) => r.id !== runId));
    } catch (_) { /* non-fatal */ }
  }, [_wfIdFor]);

  // Quick-stop a running run from the history list.  Works in both the
  // per-workflow history and the all-runs list (workflow_id resolved per
  // run).  Optimistically reflects the cancel; the poll / Refresh confirms.
  const handleStopRun = useCallback(async (runId) => {
    const wfId = _wfIdFor(runId);
    if (!wfId || !onStop) return;
    const ok = await onStop(wfId, runId);
    if (ok !== false) {
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId ? { ...r, status: 'cancelled' } : r
        )
      );
    }
  }, [_wfIdFor, onStop]);

  const pollRef = useRef(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const url = workflowId
        ? `${API_BASE}/workflows/${workflowId}/runs?limit=20`
        : `${API_BASE}/workflows/runs?limit=50`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setRuns(data.runs || []);
    } catch (_) {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  /* Initial load + reload when workflowId changes */
  useEffect(() => {
    setRuns([]);
    fetchRuns();
  }, [fetchRuns]);

  /* Poll while a run is in progress so the list stays live */
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (running) {
      pollRef.current = setInterval(fetchRuns, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [running, fetchRuns]);

  /* Refresh once after a run completes */
  const prevRunning = useRef(running);
  useEffect(() => {
    if (prevRunning.current && !running) fetchRuns();
    prevRunning.current = running;
  }, [running, fetchRuns]);

  const handleRerun = useCallback(
    async (runId) => {
      const wfId = _wfIdFor(runId);
      if (!wfId) return;
      setRerunningId(runId);
      try {
        await onRerun(wfId, runId);
      } finally {
        setRerunningId(null);
      }
    },
    [_wfIdFor, onRerun],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header — on mobile the title is omitted because the containing
          sheet already shows it (e.g. "History"); only the Refresh control
          is kept, right-aligned, to avoid a duplicated title. */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: isMobile ? 'flex-end' : 'space-between',
        padding: '8px 10px', borderBottom: '1px solid var(--border-color)',
        flexShrink: 0,
      }}>
        {!isMobile && (
          <span style={{
            fontSize: '0.76rem', fontWeight: 600,
            color: 'var(--text-secondary)',
          }}>
            {workflowId ? 'Run History' : 'All Run History'}
          </span>
        )}
        <button
          type="button"
          onClick={fetchRuns}
          disabled={loading}
          style={{
            fontSize: '0.68rem', padding: '2px 7px',
            border: '1px solid var(--border-color)', borderRadius: 4,
            background: 'transparent', cursor: loading ? 'default' : 'pointer',
            color: 'var(--text-secondary)',
          }}
        >
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {/* Run list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading && runs.length === 0 ? (
          <div style={{
            padding: 16, fontSize: '0.78rem',
            color: 'var(--text-secondary)', textAlign: 'center',
          }}>
            Loading…
          </div>
        ) : runs.length === 0 ? (
          <div style={{
            padding: 20, fontSize: '0.78rem',
            color: 'var(--text-secondary)', textAlign: 'center',
            lineHeight: 1.6,
          }}>
            {workflowId
            ? 'No runs yet. Hit Run to execute this workflow for the first time.'
            : 'No workflow runs yet. Select a workflow and hit Run to get started.'}
          </div>
        ) : (
          runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              nodes={nodes}
              onRerun={handleRerun}
              rerunning={rerunningId === run.id}
              onRename={handleRenameRun}
              onDelete={handleDeleteRun}
              onStop={handleStopRun}
              showWorkflowName={!workflowId}
              onSelectWorkflow={!workflowId ? onSelectWorkflow : undefined}
              isMobile={isMobile}
            />
          ))
        )}
      </div>
    </div>
  );
}
